import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FocusTrapDirective } from '../directives/focus-trap.directive';

/**
 * Generic two-step "are you sure?" confirm dialog — click a trigger elsewhere,
 * set `[open]="true"`, only `(confirmed)` runs the real action. Modeled on the
 * legacy `client/libs/ui/src/lib/delete-confirmation-modal` pattern (a plain
 * Yes/No dialog, no typed-confirmation-word or hold-to-confirm), rebuilt as a
 * standalone Tailwind component so it can nest on top of another modal
 * (e.g. layered over a review modal for a destructive sub-action).
 *
 * Anything projected via `<ng-content>` renders between the message and the
 * footer buttons, so callers with richer bodies (info cards, a form) can keep
 * that markup while still getting the shared backdrop/focus-trap/footer.
 */
@Component({
  selector: 'cnt-confirm-dialog',
  standalone: true,
  imports: [FocusTrapDirective],
  templateUrl: './confirm-dialog.component.html',
})
export class ConfirmDialogComponent {
  @Input() open = false;
  @Input() title = 'Are you sure?';
  @Input() message = '';
  @Input() confirmLabel = 'Yes';
  @Input() cancelLabel = 'No';
  @Input() confirmDisabled = false;
  @Input() cancelDisabled = false;
  /** Material symbol name shown in the header icon badge; pass '' to hide it. */
  @Input() icon = 'warning';
  @Input() size: 'md' | 'lg' = 'md';

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  confirm(): void { this.confirmed.emit(); }
  cancel(): void { this.cancelled.emit(); }
}
